import { useLocalization } from '@experiences/locales';
import { UiProgressButton } from '@experiences/ui-common';
import { useRouteResolver } from '@experiences/util';
import {
    Button,
    TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import type { FC } from 'react';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import {
    Controller,
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import useSWR from 'swr';

import { accountSetting } from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import {
    addOnPremOrganization,
    getAllOnPremOrganizations,
    onPremOrganizationManagementUri,
} from '../../../services/organization/OnPremOrganizationService';
import { mapOnPremOrganizationDto } from '../../../util/OnPremOrganizationUtil';
import validateAccountLogicalName from '../../../util/validators/AccountLogicalNameValidator';
import validateEmail from '../../../util/validators/EmailValidator';
import type { IEditPasswordData } from '../../common/EditPasswordFormComponent';
import EditPasswordFormComponent, { defaultEditPasswordData } from '../../common/EditPasswordFormComponent';
import UiForm from '../../common/UiForm';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../../organizationsettings/AdminBreadCrumbs';
import type { IOnPremOrganizationResponse } from '../subcomponents/OrganizationGridComponent';

interface IAddOrganizationData extends IEditPasswordData {
    organizationName: string;
    adminUserName: string;
    adminFirstName: string;
    adminLastName: string;
    adminEmail: string;
}

const useStyles = makeStyles((theme) =>
    createStyles({
        input: { marginTop: 20 },
        inputLine: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        inputLabel: {
            fontWeight: 600,
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        inputMargin: {
            marginLeft: '24px',
            marginBottom: '12px',
        },
        inlineIcon: {
            verticalAlign: 'middle',
            paddingBottom: '3px',
        },
        groupColumn: {
            display: 'flex',
            flexDirection: 'column',
        },
        inputEndAdornment: { color: theme.palette.semantic.colorForegroundDisable },
        cancelButton: { marginRight: '10px' },
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        labelGroup: {
            display: 'flex',
            flexDirection: 'column',
        },
        subText: { color: theme.palette.semantic.colorForegroundDisable },
        standaloneInfo: {
            marginLeft: '24px',
            marginBottom: '12px',
            display: 'flex',
            backgroundColor: theme.palette.semantic.colorInfoBackground,
            borderColor: theme.palette.semantic.colorInfoForeground,
            borderWidth: '1px',
            borderStyle: 'solid',
        },
        infoIcon: {
            color: theme.palette.semantic.colorInfoForeground,
            margin: '12px 9.5px 0px 9.5px',
        },
        textBox: {
            margin: '10px 10px 10px 0px',
            color: theme.palette.semantic.colorForeground,
        },
        removeLicenseAllocationButton: { margin: '0px 0px 12px 24px' },
    }),
);

const HostOrganizationAdd: FC = () => {

    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const history = useHistory();
    const getRoute = useRouteResolver();
    const organizationName = useOrganizationName();

    const [ loading, setLoading ] = useState(false);
    const [ takenUsername, setTakenUsername ] = useState<string>();
    const methods = useForm<IAddOrganizationData>({
        mode: 'onSubmit',
        defaultValues: {
            ...defaultEditPasswordData,
            organizationName: '',
            adminUserName: '',
            adminFirstName: '',
            adminLastName: '',
            adminEmail: '',
        },
    });
    const language = useLocalization();
    const {
        formState, control, errors, trigger, handleSubmit,
    } = useMemo(() => methods, [ methods ]);

    const { isDirty } = formState;

    const { mutate } = useSWR<IOnPremOrganizationResponse>([ `${onPremOrganizationManagementUri}/getAllOrganizations` ], getAllOnPremOrganizations);

    const isOrganizationNameTaken = useCallback(async (organizationName: string) => {
        const response = await getAllOnPremOrganizations();
        return response.results.some(result => result.name?.toLowerCase() === organizationName.toLowerCase());
    }, []);

    const onSubmit = useCallback(
        async (data: IAddOrganizationData) => {
            setLoading(true);
            try {
                if (await isOrganizationNameTaken(data.organizationName)) {
                    setTakenUsername(data.organizationName);
                    setLoading(false);
                    trigger('organizationName');
                    return;
                }
                await addOnPremOrganization(mapOnPremOrganizationDto(data, language));
                setLoading(false);
                mutate();
                history.push(getRoute(RouteNames.OrganizationAdminHome));
            } catch (error) {
                setLoading(false);
            }
        },
        [ getRoute, history, isOrganizationNameTaken, language, mutate, trigger ],
    );

    const breadCrumbLinks = [
        {
            link: RouteNames.OrganizationAdminHome,
            name: organizationName,
        },
        {
            link: RouteNames.HostOrganizationAdd,
            name: translate({ id: 'CLIENT_ADD_ORGANIZATION' }),
        },
    ];

    return (
        <UiPageContainer
            header={AdminBreadCrumbs(breadCrumbLinks)}
            disableGutter={[ 'top' ]}
            maxWidth='1100px'>
            <UiForm
                onSubmit={handleSubmit(onSubmit)}
                actions={
                    <div className={classes.actions}>
                        <Button
                            className={classes.cancelButton}
                            onClick={history.goBack}
                            color="primary">
                            {translate({ id: 'CLIENT_CANCEL' })}
                        </Button>
                        <UiProgressButton
                            type="submit"
                            loading={loading}
                            disabled={!isDirty}
                            variant="contained"
                            data-cy="add-organization-submit-button"
                        >
                            {translate({ id: 'CLIENT_SAVE' })}
                        </UiProgressButton>
                    </div>
                }
                isDrawer
            >
                <>
                    <div className={classes.input}>
                        <Controller
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            label={translate({ id: 'CLIENT_ORGANIZATION_NAME' })}
                            as={TextField}
                            control={control}
                            required
                            rules={{
                                required: true,
                                maxLength: accountSetting.accountLogicalNameLength,
                                validate: {
                                    available: value => value !== takenUsername,
                                    valid: value => validateAccountLogicalName(value),
                                },
                            }}
                            error={!!errors.organizationName}
                            helperText={
                                (errors.organizationName?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }))
                                || (errors.organizationName?.type === 'available' && translate({ id: 'CLIENT_ORGANIZATION_NAME_TAKEN_ERROR' }))
                                || ((errors.organizationName?.type === 'valid' || errors.organizationName?.type === 'maxLength')
                                    && translate(
                                        { id: 'CLIENT_INVALID_ACCOUNT_LOGICAL_NAME_ORG' },
                                        { 0: accountSetting.accountLogicalNameLength },
                                    ))
                            }
                            name="organizationName"
                            variant="outlined"
                            autoComplete="off"
                            fullWidth
                            InputProps={{ className: 'Tall' }}
                            InputLabelProps={{ id: 'organizationNameLabel' }}
                            inputProps={{ 'aria-labelledby': 'organizationNameLabel' }}
                            data-cy="add-organization-name"
                        />
                    </div>
                    <div className={classes.input}>
                        <Controller
                            label={translate({ id: 'CLIENT_ADMIN_USERNAME' })}
                            as={TextField}
                            control={control}
                            required
                            rules={{ required: true }}
                            error={!!errors.adminUserName}
                            helperText={errors.adminUserName?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })}
                            name="adminUserName"
                            variant="outlined"
                            autoComplete="off"
                            fullWidth
                            InputProps={{ className: 'Tall' }}
                            InputLabelProps={{ id: 'usernameLabel' }}
                            inputProps={{ 'aria-labelledby': 'usernameLabel' }}
                            data-cy="add-organization-admin-username"
                        />
                    </div>
                    <div className={classes.input}>
                        <Controller
                            label={translate({ id: 'CLIENT_ADMIN_FIRST_NAME' })}
                            as={TextField}
                            control={control}
                            error={!!errors.adminFirstName}
                            helperText={
                                errors.adminFirstName?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })
                            }
                            required
                            rules={{ required: true }}
                            name="adminFirstName"
                            variant="outlined"
                            autoComplete="off"
                            fullWidth
                            InputProps={{ className: 'Tall' }}
                            InputLabelProps={{ id: 'firstNameLabel' }}
                            inputProps={{ 'aria-labelledby': 'firstNameLabel' }}
                            data-cy="add-organization-admin-first-name"
                        />
                    </div>
                    <div className={classes.input}>
                        <Controller
                            label={translate({ id: 'CLIENT_ADMIN_LAST_NAME' })}
                            as={TextField}
                            control={control}
                            error={!!errors.adminLastName}
                            helperText={errors.adminLastName?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' })}
                            name="adminLastName"
                            variant="outlined"
                            autoComplete="off"
                            fullWidth
                            required
                            rules={{ required: true }}
                            InputProps={{ className: 'Tall' }}
                            InputLabelProps={{ id: 'lastNameLabel' }}
                            inputProps={{ 'aria-labelledby': 'lastNameLabel' }}
                            data-cy="add-organization-admin-last-name"
                        />
                    </div>
                    <div className={classes.input}>
                        <Controller
                            label={translate({ id: 'CLIENT_ADMIN_EMAIL' })}
                            as={TextField}
                            control={control}
                            required
                            rules={{
                                required: true,
                                validate: { valid: value => validateEmail(value) },
                            }}
                            error={!!errors.adminEmail}
                            helperText={
                                errors.adminEmail
                                    ? (errors.adminEmail?.type === 'valid' && translate({ id: 'CLIENT_INVALID_EMAIL_ERROR' })) ||
                    (errors.adminEmail?.type === 'required' && translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }))
                                    : translate({ id: 'CLIENT_EMAIL_HELPER_TEXT' })
                            }
                            name="adminEmail"
                            type="email"
                            variant="outlined"
                            autoComplete="off"
                            fullWidth
                            InputProps={{ className: 'Tall' }}
                            InputLabelProps={{ id: 'adminEmailLabel' }}
                            inputProps={{ 'aria-labelledby': 'adminEmailLabel' }}
                            data-cy="add-organization-admin-email"
                        />
                    </div>
                    <FormProvider {...methods}>
                        <EditPasswordFormComponent
                            respectPasswordRequirements
                            required
                            passwordLabel={translate({ id: 'CLIENT_ADMIN_PASSWORD' })}
                        />
                    </FormProvider>
                </>
            </UiForm>
        </UiPageContainer>
    );
};

export default HostOrganizationAdd;
